import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Loader2, MailCheck, ShieldCheck } from 'lucide-react';

import { MrBubbles } from '@/components/tour/MrBubbles';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SUPPORT_PHONE, SUPPORT_TEL_HREF } from '@/lib/constants';

/**
 * /welcome-back — PUBLIC front door for existing customers moving to the new
 * portal (linked from the switch-on + Bill & Pay cutover invite emails).
 *
 *   "I have an existing account" → enter old login (B&P username or email) →
 *     claim-start matches it → we email a 6-digit code to the address on file →
 *     enter code → claim-verify → redirect to /set-password → in.
 *   Can't get into that inbox? → answer three questions (an invoice number from
 *     the last 12 months, its total, a delivery ZIP) → claim-answer → a code
 *     goes to THEIR email → the same code step → in, as a member; the on-file
 *     email is told a login was added.
 *   "I'm new / that's not me"     → lightweight request-access form → admin queue.
 *
 * Security: knowing the login is not enough — the emailed code proves control
 * of the on-file email (claim-verify), or the answers prove knowledge of the
 * account's invoices and addresses. See docs/ONBOARDING.md.
 */

type Panel = 'choose' | 'login' | 'code' | 'questions' | 'request';
type Info = { kind: 'pending' | 'notfound' | 'noemail' | 'throttled'; business?: string; canAnswer?: boolean } | null;

export default function WelcomeBack() {
  const [panel, setPanel] = useState<Panel>('choose');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<Info>(null);

  // existing-account claim
  const [login, setLogin] = useState('');
  const [claim, setClaim] = useState<{ id: string; business: string; masked: string } | null>(null);
  const [code, setCode] = useState('');
  // answer-the-questions claim: the account found by claim-start
  const [lookup, setLookup] = useState<{ id: string; business: string } | null>(null);
  const [qa, setQa] = useState({ name: '', email: '', invoice: '', amount: '', zip: '' });
  const [qaLocked, setQaLocked] = useState(false);

  // request access
  const [req, setReq] = useState({ business: '', contact: '', email: '', phone: '', note: '', website: '' });
  const [reqSent, setReqSent] = useState(false);

  async function startClaim(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setInfo(null);
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/claim-start', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ login: login.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErr(data.error ?? 'Something went wrong. Please try again.');
        return;
      }
      setLookup(data.questions && data.claimId ? { id: data.claimId, business: data.businessName } : null);
      if (data.status === 'code_sent') {
        setClaim({ id: data.claimId, business: data.businessName, masked: data.maskedEmail });
        setPanel('code');
      } else if (data.status === 'pending_setup') {
        setInfo({ kind: 'pending', business: data.businessName });
      } else if (data.status === 'no_email_on_file') {
        setInfo({ kind: 'noemail', business: data.businessName, canAnswer: !!(data.questions && data.claimId) });
      } else if (data.status === 'throttled') {
        setInfo({ kind: 'throttled' });
      } else {
        setInfo({ kind: 'notfound' });
      }
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    if (!claim) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/claim-verify', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ claimId: claim.id, code: code.trim() }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErr(data.error ?? 'Incorrect code.');
        return;
      }
      // Proven — off to set a password and into the portal.
      window.location.href = data.redirect;
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function submitAnswers(e: React.FormEvent) {
    e.preventDefault();
    if (!lookup) return;
    setErr(null);
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/claim-answer', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ claimId: lookup.id, ...qa }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErr(data.error ?? 'Those answers don\'t match our records.');
        if (data.remaining === 0) setQaLocked(true);
        return;
      }
      // Right answers: a code went to THEIR email. Same code step as the email path.
      // The lookup is used up, so the "answer questions instead" link goes away.
      setLookup(null);
      setClaim({ id: data.claimId, business: data.businessName, masked: data.maskedEmail });
      setCode('');
      setPanel('code');
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  function openQuestions() {
    setErr(null);
    setInfo(null);
    setQaLocked(false);
    setPanel('questions');
  }

  async function submitRequest(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await fetch('/.netlify/functions/claim-request-access', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          businessName: req.business,
          contactName: req.contact,
          email: req.email,
          phone: req.phone,
          note: req.note,
          website: req.website, // honeypot
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.ok) {
        setErr(data.error ?? 'Please check your details and try again.');
        return;
      }
      setReqSent(true);
    } catch {
      setErr('Network error. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="relative min-h-screen w-full overflow-y-auto bg-brix-surface-alt/40 px-4 py-10">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-6 text-center">
          <img
            src="/brand/brix-logo-full.png"
            alt="Brix Beverage"
            className="mx-auto mb-3 h-10 w-auto"
            onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
          />
          <MrBubbles mood="excited" size={112} className="mx-auto mb-2" />
          <h1 className="font-display text-2xl font-bold tracking-tight text-brix-ink md:text-3xl">
            We've upgraded to a whole new experience
          </h1>
          <p className="mx-auto mt-2 max-w-md text-base leading-relaxed text-brix-muted">
            Order online, pay invoices, track deliveries, and manage your cylinders & equipment —
            all in one place.
          </p>
        </div>

        <div className="rounded-2xl border border-brix-border bg-brix-surface p-5 shadow-card md:p-6">
          {/* CHOOSE */}
          {panel === 'choose' && (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => { setPanel('login'); setInfo(null); setErr(null); }}
                className="flex w-full items-center gap-3 rounded-xl border border-brix-primary/30 bg-brix-primary-tint/40 p-4 text-left transition-colors hover:bg-brix-primary-tint/70"
              >
                <ShieldCheck size={22} className="shrink-0 text-brix-primary" />
                <div>
                  <div className="text-base font-bold text-brix-ink">I have an existing account</div>
                  <div className="text-sm text-brix-muted">
                    Billed with us before? Enter your old username or email.
                  </div>
                </div>
                <ArrowRight size={18} className="ml-auto shrink-0 text-brix-primary" />
              </button>
              <button
                type="button"
                onClick={() => { setPanel('request'); setInfo(null); setErr(null); }}
                className="flex w-full items-center gap-3 rounded-xl border border-brix-border bg-brix-surface-alt/40 p-4 text-left transition-colors hover:bg-brix-surface-alt"
              >
                <div>
                  <div className="text-base font-bold text-brix-ink">I'm new here</div>
                  <div className="text-sm text-brix-muted">Request access and we'll get you set up.</div>
                </div>
                <ArrowRight size={18} className="ml-auto shrink-0 text-brix-muted" />
              </button>
            </div>
          )}

          {/* EXISTING → enter login */}
          {panel === 'login' && (
            <form onSubmit={startClaim} className="space-y-4">
              <div>
                <h2 className="text-lg font-bold text-brix-ink">Find your account</h2>
                <p className="mt-1 text-sm text-brix-muted">
                  Enter the username or email you used on our old billing site.
                </p>
              </div>
              <div>
                <Label htmlFor="wb-login">Old username or email</Label>
                <Input
                  id="wb-login"
                  autoFocus
                  value={login}
                  onChange={(e) => setLogin(e.target.value)}
                  placeholder="you@company.com"
                  required
                />
              </div>

              {info && <InfoNote info={info} onRequest={() => setPanel('request')} onAnswer={openQuestions} />}
              {err && <p className="text-sm font-medium text-red-600">{err}</p>}

              <div className="flex items-center gap-2">
                <Button type="submit" disabled={busy || !login.trim()}>
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                  Continue
                </Button>
                <button type="button" className="text-sm font-semibold text-brix-muted hover:text-brix-ink" onClick={() => setPanel('choose')}>
                  Back
                </button>
              </div>
            </form>
          )}

          {/* EXISTING → enter code */}
          {panel === 'code' && claim && (
            <form onSubmit={verifyCode} className="space-y-4">
              <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3.5">
                <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-emerald-600" />
                <div>
                  <div className="text-base font-bold text-brix-ink">We found {claim.business}</div>
                  <div className="text-sm text-brix-muted">
                    To confirm it's you, we emailed a 6-digit code to{' '}
                    <span className="font-semibold text-brix-ink">{claim.masked}</span>.
                  </div>
                </div>
              </div>
              <div>
                <Label htmlFor="wb-code">Verification code</Label>
                <Input
                  id="wb-code"
                  autoFocus
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="text-center text-2xl font-bold tracking-[0.4em]"
                  required
                />
              </div>
              {err && <p className="text-sm font-medium text-red-600">{err}</p>}
              <div className="flex items-center gap-2">
                <Button type="submit" disabled={busy || code.length !== 6}>
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                  Verify & continue
                </Button>
                <button type="button" className="text-sm font-semibold text-brix-muted hover:text-brix-ink" onClick={() => { setPanel('login'); setCode(''); setClaim(null); }}>
                  Not you?
                </button>
              </div>
              <p className="text-xs text-brix-muted">
                Didn't get it? Check spam, or{' '}
                <button type="button" className="font-semibold text-brix-primary underline" onClick={() => { setPanel('login'); setCode(''); }}>
                  try a different login
                </button>
                .
              </p>
              {lookup && (
                <p className="text-xs text-brix-muted">
                  Can't get into that inbox?{' '}
                  <button type="button" className="font-semibold text-brix-primary underline" onClick={openQuestions}>
                    Answer a few questions instead
                  </button>
                  .
                </p>
              )}
            </form>
          )}

          {/* EXISTING → answer questions instead of the emailed code */}
          {panel === 'questions' && lookup && (
            <form onSubmit={submitAnswers} className="space-y-3.5">
              <div>
                <h2 className="text-lg font-bold text-brix-ink">Confirm it's {lookup.business}</h2>
                <p className="mt-1 text-sm text-brix-muted">
                  Answer from one of your recent Brix invoices (any from the last 12 months). We'll then
                  email a code to <b>your</b> address to finish, and let the account's main contact know
                  you've been added.
                </p>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="qa-name">Your name</Label>
                  <Input id="qa-name" autoFocus value={qa.name} onChange={(e) => setQa({ ...qa, name: e.target.value })} required />
                </div>
                <div>
                  <Label htmlFor="qa-email">Your email</Label>
                  <Input id="qa-email" type="email" value={qa.email} onChange={(e) => setQa({ ...qa, email: e.target.value })} placeholder="you@company.com" required />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="qa-inv">Invoice number</Label>
                  <Input id="qa-inv" value={qa.invoice} onChange={(e) => setQa({ ...qa, invoice: e.target.value })} placeholder="e.g. 9989017421" required />
                </div>
                <div>
                  <Label htmlFor="qa-amt">That invoice's total</Label>
                  <Input id="qa-amt" inputMode="decimal" value={qa.amount} onChange={(e) => setQa({ ...qa, amount: e.target.value })} placeholder="e.g. 482.50" required />
                </div>
              </div>
              <div>
                <Label htmlFor="qa-zip">ZIP code of an address we deliver to</Label>
                <Input id="qa-zip" inputMode="numeric" maxLength={10} value={qa.zip} onChange={(e) => setQa({ ...qa, zip: e.target.value })} placeholder="94501" required className="sm:max-w-[10rem]" />
              </div>
              {err && <p className="text-sm font-medium text-red-600">{err}</p>}
              {qaLocked && (
                <p className="text-sm text-brix-muted">
                  Call us at <a href={SUPPORT_TEL_HREF} className="font-semibold text-brix-primary">{SUPPORT_PHONE}</a> and we'll get you in.
                </p>
              )}
              <div className="flex items-center gap-2 pt-1">
                <Button type="submit" disabled={busy || qaLocked}>
                  {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                  Continue
                </Button>
                <button type="button" className="text-sm font-semibold text-brix-muted hover:text-brix-ink" onClick={() => { setErr(null); setPanel(claim ? 'code' : 'login'); }}>
                  Back
                </button>
              </div>
            </form>
          )}

          {/* NEW → request access */}
          {panel === 'request' && (
            reqSent ? (
              <div className="py-4 text-center">
                <MailCheck size={40} className="mx-auto mb-3 text-emerald-600" />
                <h2 className="text-lg font-bold text-brix-ink">Thanks — we've got it</h2>
                <p className="mx-auto mt-1 max-w-sm text-sm text-brix-muted">
                  Our team will set you up and email you next steps. Need it sooner? Call us at{' '}
                  <a href={SUPPORT_TEL_HREF} className="font-semibold text-brix-primary">{SUPPORT_PHONE}</a>.
                </p>
              </div>
            ) : (
              <form onSubmit={submitRequest} className="space-y-3.5">
                <div>
                  <h2 className="text-lg font-bold text-brix-ink">Request access</h2>
                  <p className="mt-1 text-sm text-brix-muted">Tell us who you are and we'll get you set up.</p>
                </div>
                {/* honeypot */}
                <input type="text" tabIndex={-1} autoComplete="off" value={req.website} onChange={(e) => setReq({ ...req, website: e.target.value })} className="hidden" aria-hidden />
                <div>
                  <Label htmlFor="wb-biz">Business name</Label>
                  <Input id="wb-biz" value={req.business} onChange={(e) => setReq({ ...req, business: e.target.value })} placeholder="Your business" />
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="wb-contact">Your name</Label>
                    <Input id="wb-contact" value={req.contact} onChange={(e) => setReq({ ...req, contact: e.target.value })} />
                  </div>
                  <div>
                    <Label htmlFor="wb-phone">Phone</Label>
                    <Input id="wb-phone" value={req.phone} onChange={(e) => setReq({ ...req, phone: e.target.value })} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="wb-email">Email *</Label>
                  <Input id="wb-email" type="email" required value={req.email} onChange={(e) => setReq({ ...req, email: e.target.value })} placeholder="you@company.com" />
                </div>
                <div>
                  <Label htmlFor="wb-note">Anything else? (optional)</Label>
                  <Input id="wb-note" value={req.note} onChange={(e) => setReq({ ...req, note: e.target.value })} placeholder="Account #, location, etc." />
                </div>
                {err && <p className="text-sm font-medium text-red-600">{err}</p>}
                <div className="flex items-center gap-2 pt-1">
                  <Button type="submit" disabled={busy || !req.email.trim()}>
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
                    Request access
                  </Button>
                  <button type="button" className="text-sm font-semibold text-brix-muted hover:text-brix-ink" onClick={() => setPanel('choose')}>
                    Back
                  </button>
                </div>
                <p className="pt-1 text-xs text-brix-muted">
                  Prefer a full application?{' '}
                  <Link to="/apply" className="font-semibold text-brix-primary underline">Apply for an account</Link>.
                </p>
              </form>
            )
          )}
        </div>

        <p className="mt-5 text-center text-sm text-brix-muted">
          Already set up?{' '}
          <Link to="/login" className="font-semibold text-brix-primary underline">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

function InfoNote({ info, onRequest, onAnswer }: { info: NonNullable<Info>; onRequest: () => void; onAnswer: () => void }) {
  const map: Record<NonNullable<Info>['kind'], { title: string; body: string; cta?: boolean }> = {
    pending: {
      title: `We found ${info.business ?? 'your account'} — almost there`,
      body: "It's not switched on for the new portal yet. We've let our team know and we'll email you when it's ready.",
      cta: true,
    },
    noemail: {
      title: `We found ${info.business ?? 'your account'}`,
      body: info.canAnswer
        ? "We don't have an email on file to send a code to. You can confirm it's you with details from a recent invoice instead."
        : "We don't have an email on file to verify it's you. Request access and we'll reach out to confirm.",
      cta: true,
    },
    notfound: {
      title: "We couldn't find that account",
      body: "Double-check your old username or email — or request access and we'll set you up.",
      cta: true,
    },
    throttled: {
      title: 'Too many attempts',
      body: 'Please wait a few minutes and try again.',
    },
  };
  const m = map[info.kind];
  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50 p-3.5">
      <div className="text-sm font-bold text-amber-900">{m.title}</div>
      <div className="mt-0.5 text-sm text-amber-800">{m.body}</div>
      {info.kind === 'noemail' && info.canAnswer ? (
        <button type="button" onClick={onAnswer} className="mt-2 text-sm font-semibold text-brix-primary underline">
          Confirm it's you with a recent invoice →
        </button>
      ) : m.cta && (
        <button type="button" onClick={onRequest} className="mt-2 text-sm font-semibold text-brix-primary underline">
          Request access →
        </button>
      )}
    </div>
  );
}
